import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Sparkles,
  ListOrdered,
  Calendar,
  User,
  MapPin,
  History,
  X as XIcon,
} from "lucide-react";
import { api, type RecallEntityAwareResponse } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

const SAMPLE_QUESTIONS: Array<{ q: string; Icon: typeof Search }> = [
  { q: "How many times have I been to Toronto?", Icon: ListOrdered },
  { q: "When did I last meet Sarah?", Icon: Calendar },
  { q: "What did I commit to this month?", Icon: ListOrdered },
  { q: "What do I know about Trust Auto?", Icon: User },
  { q: "Where did I travel last quarter?", Icon: MapPin },
];

const RECENT_KEY = "memso:recall:recent";

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => typeof s === "string").slice(0, 6);
  } catch {
    return [];
  }
}

function saveRecent(items: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, 6)));
  } catch {
    /* localStorage unavailable */
  }
}

export function Recall() {
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecallEntityAwareResponse | null>(null);
  const [recent, setRecent] = useState<string[]>(() => loadRecent());

  useEffect(() => {
    setRecent(loadRecent());
  }, []);

  const ask = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) return;
      setPending(true);
      setError(null);
      try {
        const r = await api.recallEntityAware(trimmed);
        setResult(r);
        setRecent((prev) => {
          const next = [trimmed, ...prev.filter((p) => p !== trimmed)].slice(0, 6);
          saveRecent(next);
          return next;
        });
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setPending(false);
      }
    },
    [],
  );

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      ask(query);
    },
    [ask, query],
  );

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Recall" }]} />

      <header className="space-y-1">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Recall
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Ask anything. Memso answers from your own data, with citations.
        </p>
      </header>

      <TeachingBanner
        id="recall-intro"
        title="What is this?"
        body="Recall is the ask-anything surface. It uses your claim
              ledger so counts are deduped (a six-day Toronto + Kitchener
              trip is one trip, not 38 mentions). Each answer cites the
              evidence packets so you can drill in."
      />

      <form onSubmit={onSubmit} className="relative">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask Memso..."
          autoComplete="off"
          className="w-full rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 pl-12 pr-32 py-4 text-lg font-medium text-slate-900 dark:text-slate-100 focus:border-sky-500 focus:outline-none transition"
        />
        <button
          type="submit"
          disabled={pending || !query.trim()}
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 transition"
        >
          <Sparkles className="w-4 h-4" />
          {pending ? "..." : "Ask"}
        </button>
      </form>

      <section aria-labelledby="suggested" className="space-y-2">
        <h2 id="suggested" className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Try one of these
        </h2>
        <div className="flex flex-wrap gap-1.5">
          {SAMPLE_QUESTIONS.map(({ q, Icon }) => (
            <button
              key={q}
              type="button"
              onClick={() => {
                setQuery(q);
                ask(q);
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-800/60 hover:bg-slate-200 dark:hover:bg-slate-700/60 text-slate-700 dark:text-slate-200 px-3 py-1.5 text-xs font-medium transition"
            >
              <Icon className="w-3.5 h-3.5" />
              {q}
            </button>
          ))}
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {result && <AnswerCard result={result} />}

      {recent.length > 0 && (
        <section aria-labelledby="recent" className="space-y-2">
          <h2 id="recent" className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Recent
          </h2>
          <ul className="space-y-1">
            {recent.map((r) => (
              <li key={r} className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-400" />
                <button
                  type="button"
                  onClick={() => {
                    setQuery(r);
                    ask(r);
                  }}
                  className="text-sm text-sky-600 dark:text-sky-400 hover:underline truncate"
                >
                  {r}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = recent.filter((q) => q !== r);
                    setRecent(next);
                    saveRecent(next);
                  }}
                  aria-label="Remove"
                  className="ml-auto text-slate-400 hover:text-rose-500"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function AnswerCard({ result }: { result: RecallEntityAwareResponse }) {
  if (!result.ok || result.fellBack) {
    return (
      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-5 space-y-2">
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Memso could not answer that with an entity-aware claim.
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {result.reason || "Try rephrasing as a question about a person, place, or count."}
        </p>
      </div>
    );
  }
  const agg = (result.aggregation || {}) as Record<string, unknown>;
  const count = typeof agg.count === "number" ? (agg.count as number) : null;
  const range = typeof agg.range === "string" ? (agg.range as string) : null;
  return (
    <article className="rounded-2xl border border-sky-200/70 dark:border-sky-700/40 bg-gradient-to-br from-sky-50/80 to-white dark:from-sky-900/30 dark:to-slate-900/40 p-5 space-y-3">
      <div className="flex items-baseline gap-3 flex-wrap">
        {count !== null && (
          <span className="text-4xl font-bold text-sky-600 dark:text-sky-400 tabular-nums">{count}</span>
        )}
        <div className="space-y-0.5 min-w-0">
          <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {result.answer || "Answer not narrated"}
          </p>
          {result.query && (
            <p className="text-xs text-slate-500 dark:text-slate-400 italic">"{result.query}"</p>
          )}
        </div>
      </div>
      {range && (
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {range}
        </p>
      )}
      {result.resolved && result.resolved.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {result.resolved.map((r) => (
            <span
              key={r.id}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 px-2 py-0.5 text-xs font-semibold"
            >
              {r.type}: {r.display_name}
            </span>
          ))}
        </div>
      )}
      {result.citations && result.citations.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Evidence ({result.citations.length})
          </p>
          <ul className="space-y-1">
            {result.citations.slice(0, 8).map((c) => (
              <li key={c.ref_id} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <span className="inline-flex shrink-0 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-1.5 py-0.5 text-[10px] font-mono">
                  {c.ref_kind}
                </span>
                <span className="truncate">{c.label}</span>
                {c.date && <span className="ml-auto text-slate-400 shrink-0">{c.date}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[10px] uppercase tracking-wider text-slate-400">
        Source: {result.answer_source || "deterministic"} {result.intent && `· intent ${result.intent}`}
      </p>
    </article>
  );
}
